"use client";

import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, RotateCcw, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AIVoiceInput } from "@/components/ai-voice-input";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import { normalizeChosenExplanationText } from "@/lib/vocabulary/chosen-explanation";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface NarrationModeIndicatorCardProps {
  term: string;
  scenarioName?: string;
  isPlaying: boolean;
  controlsDisabled?: boolean;
  onSkip?: () => void;
  onTogglePlayPause?: () => void;
  onReplay?: () => void;
  onPracticeSpeaking?: () => void;
  containerClassName?: string;
  cardClassName?: string;
  disableEntranceAnimation?: boolean;
  explanation?: string;
  isGeneratingAudio?: boolean;
  isGeneratingExplanation?: boolean;
}

export const NarrationModeIndicatorCard: React.FC<
  NarrationModeIndicatorCardProps
> = ({
  term,
  scenarioName,
  isPlaying,
  controlsDisabled = false,
  onSkip,
  onTogglePlayPause,
  onReplay,
  onPracticeSpeaking,
  containerClassName,
  cardClassName,
  disableEntranceAnimation = false,
  explanation,
  isGeneratingAudio = false,
  isGeneratingExplanation = false,
}) => {
  const { t } = useI18n();
  const normalizedExplanation = normalizeChosenExplanationText(explanation) || undefined;

  return (
    <div className={cn("relative w-full bg-muted", containerClassName)}>
      <motion.div
        initial={
          disableEntranceAnimation ? false : { opacity: 0, scale: 0.96, y: 12 }
        }
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={
          disableEntranceAnimation ? undefined : { opacity: 0, scale: 0.96, y: 12 }
        }
        transition={{ duration: 0.25, ease: [0.26, 0.08, 0.25, 1] }}
        className={cn(
          "pointer-events-auto relative rounded-[40px] bg-foreground/70 backdrop-blur-sm shadow-2xl transition-all duration-200 dark:bg-white/30",
          cardClassName
        )}
      >
        <div className="flex items-center justify-between px-4 pt-3">
          <div className="flex-1">
            <AnimatePresence initial={false}>
              {/* {scenarioName && (
                <motion.span
                  key={scenarioName}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="rounded-full bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-white/60"
                >
                  {scenarioName}
                </motion.span>
              )} */}
            </AnimatePresence>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            className="rounded-xl text-xs text-white/80 dark:text-black/80 hover:bg-transparent hover:text-white/80 dark:hover:text-black/80 active:bg-transparent focus:bg-transparent"
          >
            {t("mobileImmersive.narration.skip", "Skip")}
          </Button>
        </div>

        <AIVoiceInput
          demoMode={false}
          visualizerBars={48}
          className="px-6 pb-4 bg-transparent"
          colorScheme="light"
          playingText={t(
            "mobileImmersive.narration.playing",
            "Playing narration..."
          )}
          readyText={t("mobileImmersive.narration.ready", "Narration ready")}
          isPlaying={isPlaying}
        />

        <div className="px-6 pb-5 text-center text-white dark:text-black">
          {/* <div className="text-[11px] uppercase tracking-[0.28em] text-white/70">
            {t("mobileImmersive.narration.currentTerm", "Current term")}
          </div> */}
          <div className="mt-0 font-sans text-lg font-semibold text-white dark:text-black">
            {term}
          </div>
          {isGeneratingExplanation && !normalizedExplanation ? (
            <div className="mt-2 w-full text-center">
              <div className="inline-flex items-center gap-2 text-xs text-white/70 dark:text-black/70">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{t("mobileImmersive.narration.generatingExplanation", "Generating explanation...")}</span>
              </div>
            </div>
          ) : normalizedExplanation ? (
            <div className="mt-2 w-full text-left cursor-default select-text">
              {isPlaying ? (
              <TextGenerateEffect
                key={`${term}-${isPlaying ? "playing" : "idle"}`}
                words={normalizedExplanation}
                className="text-xs leading-relaxed text-white/85 dark:text-black/85"
                wordClassName="text-white/85 dark:text-black/85"
                duration={0.45}
              />
            ) : (
              <p className="text-xs leading-relaxed text-white/85 dark:text-black/85">
                {normalizedExplanation}
              </p>
            )}
            </div>
          ) : null}
          <div className="mt-3 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={onTogglePlayPause}
              disabled={controlsDisabled}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/60 dark:border-black/20 bg-white/80 dark:bg-black/10 px-3 text-xs font-normal text-black dark:text-black shadow-sm hover:-translate-y-0.5 hover:bg-white dark:hover:bg-black/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
            {isGeneratingAudio ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-black dark:text-black" />
            ) : isPlaying ? (
                <Pause className="h-3.5 w-3.5 text-black dark:text-black" />
            ) : (
                <Play className="h-3.5 w-3.5 text-black dark:text-black" />
            )}
            <span>
                {isGeneratingAudio
                    ? t("mobileImmersive.narration.generating", "Generating audio...")
                    : isPlaying
                    ? t("mobileImmersive.narration.pause", "Pause narration")
                    : t("mobileImmersive.narration.resume", "Resume narration")}
            </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={onReplay}
              disabled={controlsDisabled}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-white/60 dark:border-black/20 bg-white/80 dark:bg-black/10 px-3 text-xs font-normal text-black dark:text-black shadow-sm hover:-translate-y-0.5 hover:bg-white dark:hover:bg-black/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RotateCcw className="h-3.5 w-3.5 text-black dark:text-black" />
              <span>
                {t("mobileImmersive.narration.repeat", "Replay narration")}
              </span>
            </Button>
            {onPracticeSpeaking && (
              <button
                type="button"
                onClick={onPracticeSpeaking}
                className="flex size-10 items-center justify-center overflow-hidden rounded-xl transition-colors hover:bg-orange-500/20 active:bg-orange-500/30"
                aria-label="Practice speaking with this term"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 256 256"
                  fill="none"
                  role="img"
                  aria-hidden="true"
                  className="w-full h-full"
                  style={{ 
                    transition: 'transform 0.8s ease-in-out',
                    transformOrigin: 'center center'
                  }}
                >
                  <circle cx="128" cy="128" r="128" fill="#F97316"></circle>
                  <ellipse 
                    cx="100.5546299346353" 
                    cy="140.5667763724447" 
                    rx="18" 
                    ry="18" 
                    fill="#fff" 
                    className="face-icon-blink"
                  ></ellipse>
                  <ellipse 
                    cx="147.3546299346353" 
                    cy="140.5667763724447" 
                    rx="18" 
                    ry="18" 
                    fill="#fff" 
                    className="face-icon-blink"
                  ></ellipse>
                </svg>
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

interface VocabularyNarrationPlayerProps {
  term: string;
  scenarioName?: string;
  audioUrl?: string | null;
  onClose?: () => void;
  onPracticeSpeaking?: () => void;
  containerClassName?: string;
  cardClassName?: string;
  disableEntranceAnimation?: boolean;
  explanation?: string;
  onAudioMissing?: (explanation?: string) => Promise<void>;
  onExplanationMissing?: () => Promise<string | undefined>;
  episodeId?: string;
  targetLanguageCode?: string;
  isGeneratingAudio?: boolean;
  isGeneratingExplanation?: boolean;
}

export const VocabularyNarrationPlayer: React.FC<
  VocabularyNarrationPlayerProps
> = ({
  term,
  scenarioName,
  audioUrl,
  onClose,
  onPracticeSpeaking,
  containerClassName,
  cardClassName,
  disableEntranceAnimation,
  explanation,
  onAudioMissing,
  onExplanationMissing,
  episodeId,
  targetLanguageCode,
  isGeneratingAudio: externalIsGeneratingAudio,
  isGeneratingExplanation: externalIsGeneratingExplanation
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [internalIsGeneratingAudio, setInternalIsGeneratingAudio] = useState(false);
  const [internalIsGeneratingExplanation, setInternalIsGeneratingExplanation] = useState(false);
  const [generatedExplanation, setGeneratedExplanation] = useState<string | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUnlockedRef = useRef(false);
  const explanationGenerationAttemptedRef = useRef(false);
  const currentAudioUrlRef = useRef<string | null>(null);

  const isGeneratingAudio = externalIsGeneratingAudio || internalIsGeneratingAudio;
  const isGeneratingExplanation = externalIsGeneratingExplanation || internalIsGeneratingExplanation;
  
  // Use generated explanation if available, otherwise fall back to prop
  const effectiveExplanation = generatedExplanation || explanation;
  
  // Track if audio generation has been attempted for this term
  const audioGenerationAttemptedRef = useRef(false);

  // Create and unlock Audio object on mount (triggered by user gesture)
  // This is crucial for Mobile Safari autoplay support
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Only create once
    if (!audioRef.current) {
      console.log('[NarrationPlayer] Creating and unlocking Audio object for mobile');
      const audio = new Audio();
      audio.preload = "auto";
      audio.crossOrigin = "anonymous";
      audioRef.current = audio;

      // Try to "unlock" this audio object with a silent play
      // This works because the component is mounted in response to a user gesture
      audio.muted = true;
      const unlockPromise = audio.play();
      if (unlockPromise && typeof unlockPromise.then === "function") {
        unlockPromise
          .then(() => {
            audio.pause();
            audio.muted = false;
            audio.currentTime = 0;
            audioUnlockedRef.current = true;
            console.log('[NarrationPlayer] Audio unlocked successfully for mobile');
          })
          .catch(() => {
            audio.muted = false;
            console.log('[NarrationPlayer] Audio unlock not needed or context not ready');
          });
      } else {
        audio.muted = false;
      }
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
      audioUnlockedRef.current = false;
    };
  }, []);

  // Check if explanation is missing and generate it
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const normalizedExplanation = normalizeChosenExplanationText(explanation);
    
    // If explanation is missing and we haven't attempted generation yet
    if (!normalizedExplanation && !isGeneratingExplanation && onExplanationMissing && !explanationGenerationAttemptedRef.current) {
      console.log('[NarrationPlayer] Explanation missing, triggering generation...', { term });
      explanationGenerationAttemptedRef.current = true;
      setInternalIsGeneratingExplanation(true);
      
      onExplanationMissing()
        .then((result) => {
          if (result) {
            console.log('[NarrationPlayer] Explanation generated', { term, result: result.substring(0, 50) });
            setGeneratedExplanation(result);
          }
        })
        .catch((error) => {
          console.error('[NarrationPlayer] Failed to generate explanation', error);
        })
        .finally(() => {
          setInternalIsGeneratingExplanation(false);
        });
    }
  }, [explanation, onExplanationMissing, isGeneratingExplanation, term]);

  // Audio generation trigger - runs when generatedExplanation becomes available
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    console.log('[NarrationPlayer] Audio generation check', { 
      audioUrl, 
      isGeneratingAudio, 
      hasGeneratedExplanation: !!generatedExplanation,
      audioAttempted: audioGenerationAttemptedRef.current 
    });

    // Check if audio is missing and we need to generate it
    const effectiveExplanationForAudio = generatedExplanation || normalizeChosenExplanationText(explanation);
    const hasExplanation = !!effectiveExplanationForAudio;
    
    if (!audioUrl && !isGeneratingAudio && onAudioMissing && hasExplanation && !audioGenerationAttemptedRef.current) {
      console.log('[NarrationPlayer] Audio missing, triggering generation...', { 
        hasGeneratedExplanation: !!generatedExplanation,
        explanationPreview: effectiveExplanationForAudio?.substring(0, 50)
      });
      audioGenerationAttemptedRef.current = true;
      setInternalIsGeneratingAudio(true);
      // Pass the explanation to onAudioMissing so it can use the latest value
      onAudioMissing(effectiveExplanationForAudio).finally(() => {
        setInternalIsGeneratingAudio(false);
      });
    }
  }, [audioUrl, generatedExplanation, explanation, isGeneratingAudio, onAudioMissing]);

  // Audio playback - when audioUrl changes, update src and play (reusing unlocked Audio object)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      console.log('[NarrationPlayer] Audio ref not ready yet, skipping playback setup');
      return;
    }

    // If no valid audioUrl, reset state
    if (!audioUrl || typeof audioUrl !== "string") {
      setIsReady(false);
      setIsPlaying(false);
      // Don't clear the audio object, just reset its src if it had one
      if (currentAudioUrlRef.current) {
        audio.pause();
        audio.src = "";
        currentAudioUrlRef.current = null;
      }
      return;
    }

    // Skip if same URL AND audio element already has this src loaded
    // This prevents re-triggering when the URL hasn't actually changed
    // But allows re-setup after StrictMode cleanup (where audio.src gets cleared)
    const audioSrcMatches = audio.src && audio.src === audioUrl;
    if (currentAudioUrlRef.current === audioUrl && audioSrcMatches) {
      console.log('[NarrationPlayer] Same URL and already loaded, skipping', { url: audioUrl });
      return;
    }

    console.log('[NarrationPlayer] Setting audio src (reusing unlocked Audio)', { 
      url: audioUrl, 
      prevRef: currentAudioUrlRef.current,
      currentAudioSrc: audio.src 
    });
    currentAudioUrlRef.current = audioUrl;

    let hasTriggeredPlay = false;

    // Event handlers
    const handleCanPlay = () => {
      if (hasTriggeredPlay) {
        console.log('[NarrationPlayer] canplay fired but already triggered play, skipping');
        return;
      }
      hasTriggeredPlay = true;
      console.log('[NarrationPlayer] Audio ready to play (canplay event)');
      setIsReady(true);
      
      // Try to auto-play (should work on mobile since Audio object was unlocked)
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            console.log('[NarrationPlayer] Playing started');
            setIsPlaying(true);
          })
          .catch((error) => {
            console.warn("Failed to autoplay narration audio:", error);
            setIsPlaying(false);
          });
      } else {
        setIsPlaying(true);
      }
    };

    const handleCanPlayThrough = () => {
      // Also listen to canplaythrough as a fallback
      if (hasTriggeredPlay) {
        return;
      }
      console.log('[NarrationPlayer] canplaythrough fired as fallback');
      handleCanPlay();
    };

    const handleLoadedData = () => {
      // Additional fallback for loadeddata event
      console.log('[NarrationPlayer] loadeddata fired, readyState:', audio.readyState);
      if (!hasTriggeredPlay && audio.readyState >= 2) {
        // If we have at least current frame data, try to play
        // Wait a tick to allow browser to update readyState
        setTimeout(() => {
          if (!hasTriggeredPlay && audio.readyState >= 3) {
            console.log('[NarrationPlayer] loadeddata fallback triggering play');
            handleCanPlay();
          }
        }, 50);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onClose?.();
    };

    const handleError = (event: Event) => {
      console.warn("Narration audio failed to play:", event);
      setIsPlaying(false);
      setIsReady(false);
      onClose?.();
    };

    // Add event listeners - use multiple events as fallback
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);
    audio.addEventListener("loadeddata", handleLoadedData);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    // Update src (this triggers loading)
    audio.src = audioUrl;
    audio.load(); // Explicitly call load() to ensure loading starts

    // If audio is already ready (e.g., from cache), handle immediately
    // Use setTimeout to allow the browser to update readyState after src change
    setTimeout(() => {
      if (!hasTriggeredPlay && audio.readyState >= 3) {
        console.log('[NarrationPlayer] Audio already ready (cached), readyState:', audio.readyState);
        handleCanPlay();
      }
    }, 0);

    return () => {
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
      audio.removeEventListener("loadeddata", handleLoadedData);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      // Reset the ref so next effect run will re-setup audio
      // This is important for React StrictMode double-invoke
      currentAudioUrlRef.current = null;
    };
  }, [audioUrl, onClose]);

  const handleSkip = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
    onClose?.();
  };

  const handleToggle = () => {
    const audio = audioRef.current;
    if (!audio || !isReady) {
      return;
    }

    if (audio.paused) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.warn("Failed to resume narration playback:", error);
            setIsPlaying(false);
          });
      } else {
        setIsPlaying(true);
      }
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleReplay = () => {
    const audio = audioRef.current;
    if (!audio || !isReady) {
      return;
    }

    try {
      audio.pause();
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.warn("Failed to replay narration audio:", error);
            setIsPlaying(false);
          });
      } else {
        setIsPlaying(true);
      }
    } catch (error) {
      console.warn("Failed to replay narration audio:", error);
    }
  };

  return (
    <NarrationModeIndicatorCard
      term={term}
      scenarioName={scenarioName}
      isPlaying={isPlaying}
      controlsDisabled={!isReady && !isGeneratingAudio}
      onSkip={handleSkip}
      onTogglePlayPause={handleToggle}
      onReplay={handleReplay}
      onPracticeSpeaking={onPracticeSpeaking}
      containerClassName={containerClassName}
      cardClassName={cardClassName}
      disableEntranceAnimation={disableEntranceAnimation}
      explanation={effectiveExplanation}
      isGeneratingAudio={isGeneratingAudio}
      isGeneratingExplanation={isGeneratingExplanation}
    />
  );
};

